import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js"
import styles from "./styles.module.scss"
import { colors } from "@theme/colors"
import ArrowBack from "~icons/eva/arrow-back-fill"
import AlertCircle from "~icons/eva/alert-circle-outline"
import ChevronDown from "~icons/eva/chevron-down-outline"
import {
  createStripeSubscription,
  setStripeDefaultPaymentMethod,
} from "../../../../../services/firebase-functions/billing"
import { PaymentIntentResult, SetupIntentResult } from "@stripe/stripe-js"
import { useMutation } from "@tanstack/react-query"
import { observer } from "mobx-react-lite"
import { makeAutoObservable } from "mobx"
import { useMemo } from "react"
import { StripePublicConfig, StripeSubscriptionStatus } from "@cruisen/contract"
import { CountriesList } from "../../../../atoms/CountriesList"
import { Popover } from "../../../../atoms/Popover"
import { DefaultSpinner } from "../../../../loaders/DefaultSpinner"
import { useStores } from "../../../../../models/helpers/useStores"
import classNames from "classnames"
import Config from "../../../../../config"
import { formatCurrency } from "@cruisen/i18n"
import { updateUser } from "../../../../../services/firestore/user"

class CreditCardDetailsStore {
  isCountrySelectorOpen: boolean
  errorMessage: string
  selectedCountry: string
  selectedCountryISO2: string
  zipCode: string

  constructor() {
    this.errorMessage = ""
    this.isCountrySelectorOpen = false
    this.selectedCountry = "United States"
    this.selectedCountryISO2 = "US"
    this.zipCode = ""

    makeAutoObservable(this)
  }

  setZipCode(zipCode: string) {
    this.zipCode = zipCode
  }

  setSelectedCountryISO2(iso2: string) {
    this.selectedCountryISO2 = iso2
  }

  setErrorMessage(message: string) {
    this.errorMessage = message
  }

  setIsCountrySelectorOpen(isOpen: boolean) {
    this.isCountrySelectorOpen = isOpen
  }

  setSelectedCountry(country: string) {
    this.selectedCountry = country
  }

  toggleCountrySelector() {
    this.isCountrySelectorOpen = !this.isCountrySelectorOpen
  }
}

export const CreditCardDetails = observer(
  ({
    goBack,
    onSuccess,
    subscriptionStatus,
    stripeConfigPrice,
    promotionalCode,
  }: {
    goBack: () => void
    onSuccess: () => void
    subscriptionStatus: StripeSubscriptionStatus
    stripeConfigPrice: StripePublicConfig["prices"][0]
    promotionalCode: string
  }) => {
    const rootStore = useStores()
    const stripe = useStripe()
    const elements = useElements()
    const store = useMemo(() => new CreditCardDetailsStore(), [])

    const { mutate: onSubmit, isPending } = useMutation({
      mutationFn: async () => {
        if (!stripe) {
          // Stripe.js hasn't yet loaded.
          // Make sure to disable form submission until Stripe.js has loaded.
          return
        }

        if (!store.zipCode) {
          store.setErrorMessage("Please enter a zip code")
          return
        }

        const { error: submitError } = await elements.submit()
        if (submitError) {
          console.log("Error submitting form", { submitError })
          return
        }

        const createStripeSubscriptionResult = await createStripeSubscription({
          priceId: stripeConfigPrice.priceId,
          couponId: stripeConfigPrice.defaultCouponId,
          billingAddress: {
            country: store.selectedCountryISO2,
            postcode: store.zipCode,
          },
        })

        if (promotionalCode && stripeConfigPrice.coupons?.length > 0) {
          await updateUser(rootStore.loggedInUser?.id, {
            subscriptionCouponCode: promotionalCode,
          })
        }

        if (createStripeSubscriptionResult) {
          const { type, clientSecret, subscriptionId } = createStripeSubscriptionResult
          const confirmCard = type === "setup" ? stripe.confirmCardSetup : stripe.confirmCardPayment
          const loggedInUser = rootStore.loggedInUser

          const confirmationResult = await confirmCard(clientSecret, {
            payment_method: {
              card: elements.getElement(CardNumberElement),
              billing_details: {
                name: `${loggedInUser?.firstName} ${loggedInUser?.lastName}`,
                address: {
                  country: store.selectedCountryISO2,
                  postal_code: store.zipCode,
                },
              },
            },
          })

          if (confirmationResult.error) {
            console.log("confirmation failed", confirmationResult.error)
            if (confirmationResult.error.message) {
              store.setErrorMessage(confirmationResult.error.message)
            }
            return
          }

          const paymentMethodId =
            type === "setup"
              ? (confirmationResult as SetupIntentResult).setupIntent?.payment_method?.toString()
              : (
                  confirmationResult as PaymentIntentResult
                ).paymentIntent?.payment_method?.toString()

          const result = await setStripeDefaultPaymentMethod({ subscriptionId, paymentMethodId })

          if (result.success) {
            console.log("Go to next step")
            onSuccess()
          }
        }
      },
    })

    return (
      <div className={styles.creditCardDetailsContainer}>
        <div className={styles.heading}>
          {!rootStore.stripeSubscription?.id && (
            <button className={styles.backButton} disabled={isPending} onClick={() => goBack()}>
              <ArrowBack style={{ width: 24, height: 24 }} />
            </button>
          )}
          <p className={styles.headingText}>Credit card details</p>
        </div>
        {subscriptionStatus === "trialing" ? (
          <p className={styles.description}>
            Please provide your payment details to activate your subscription and get a{" "}
            <b>{formatCurrency(Config.stripeTrialPeriodEarlyPaymentMethodCredit / 100)} credit</b>.
          </p>
        ) : (
          <p className={styles.description}>
            Please provide your payment details to activate your subscription.
          </p>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault()
            onSubmit()
          }}
        >
          <div className={styles.cardForm}>
            <CardNumberElement
              className={styles.cardInput}
              options={{
                style: {
                  base: {
                    color: colors.textPrimary,
                    "::placeholder": { color: colors.textTertiary },
                  },
                },
                placeholder: "Card number",
              }}
              aria-label="Credit card number"
            />
            <div className={styles.cardInputRow}>
              <CardExpiryElement
                className={styles.cardInput}
                options={{
                  style: {
                    base: {
                      color: colors.textPrimary,
                      "::placeholder": { color: colors.textTertiary },
                    },
                  },
                  placeholder: "Expiry date",
                }}
                aria-label="Credit card expiry date"
              />
              <CardCvcElement
                className={styles.cardInput}
                options={{
                  style: {
                    base: {
                      color: colors.textPrimary,
                      "::placeholder": { color: colors.textTertiary },
                    },
                  },
                  placeholder: "CVV",
                }}
                aria-label="Credit card CVC"
              />
            </div>
            <div className={styles.cardInputRow}>
              <Popover
                isOpen={store.isCountrySelectorOpen}
                onClickOutside={() => store.setIsCountrySelectorOpen(false)}
                content={
                  <CountriesList
                    withPhonePrefix={false}
                    onSelect={(iso2, country) => {
                      store.setSelectedCountry(country)
                      store.setSelectedCountryISO2(iso2)
                      store.setIsCountrySelectorOpen(false)
                    }}
                  />
                }
              >
                <button
                  className={styles.cardInput}
                  type="button"
                  onClick={() => store.toggleCountrySelector()}
                >
                  <p className={styles.userCountryButtonText}>{store.selectedCountry}</p>
                  <ChevronDown className={styles.chevronDownIcon} />
                </button>
              </Popover>
              <input
                value={store.zipCode}
                onChange={(e) => store.setZipCode(e?.target?.value || "")}
                name="zipCode"
                placeholder="Zip code"
                className={classNames(styles.cardInput, styles.zipCodeInput)}
              />
            </div>
          </div>
          {store.errorMessage && (
            <div className={styles.errorMessageWrapper}>
              <AlertCircle className={styles.errorIcon} />
              <p className={styles.errorMessage}>{store.errorMessage}</p>
            </div>
          )}
          <p className={styles.trialInfo}>You can always change your credit card details later.</p>
          <button disabled={!stripe || isPending} type="submit" className={styles.submitButton}>
            {isPending ? <DefaultSpinner size="sm" /> : null}
            {isPending ? "Processing..." : "Continue"}
          </button>
        </form>
      </div>
    )
  },
)
